import { Component, ojs, app, v, state } from "modular-openscriptjs";
import {
    pageInfo,
    scrollToTop,
    either,
    isMobile,
    cleanDispose,
} from "../../../helpers";
import { api } from "../../../declarations";
import { routes } from "../../../backendRoutes";
import SolidIcon from "../../Blocks/SolidIcon";
import Spinner from "../../Blocks/Spinner";
import OffcanvasBody from "../../Offcanvas/OffcanvasBody";
import ModalBody from "../../Modals/ModalBody";
import { tool } from "../../../fotastart";
import { Chart, registerables } from "chart.js";

Chart.register(...registerables);

const h = app("h");

ojs(SolidIcon, Spinner, OffcanvasBody, ModalBody);

const getPastDate = (daysAgo) => {
    const d = new Date();
    d.setDate(d.getDate() - daysAgo);
    return d.toISOString().split("T")[0];
};

const getTodayDate = () => {
    return new Date().toISOString().split("T")[0];
};

export default class BusinessAnalytics extends Component {
    revenueChart = null;
    ordersChart = null;
    stats = state(null);
    loading = state(true);
    startDate = state(getPastDate(6));
    endDate = state(getTodayDate());
    interval = state("day");

    // Temporary states for offcanvas/modal filter inputs
    tempStartDate = state(getPastDate(6));
    tempEndDate = state(getTodayDate());
    tempInterval = state("day");
    availableIntervals = state(["day"]);

    filterHolder = null;

    constructor() {
        super();
        this.stats.listener(this);
        this.loading.listener(this);
        this.startDate.listener(this);
        this.endDate.listener(this);
        this.interval.listener(this);
        this.tempStartDate.listener(this);
        this.tempEndDate.listener(this);
        this.tempInterval.listener(this);
        this.availableIntervals.listener(this);

        // Trigger chart rendering when stats are populated
        this.stats.listener((statsVal) => {
            const value = statsVal.value;
            if (value) {
                setTimeout(() => {
                    this.initRevenueChart(
                        value.revenue_labels,
                        value.revenue_trend,
                    );
                    this.initOrdersChart(
                        value.category_stats.labels,
                        value.category_stats.data,
                    );
                }, 50);
            }
        });
    }

    render() {
        const businessName = window.currentCompany?.name || "Your Business";
        pageInfo(`Analytics - ${businessName}`);
        scrollToTop();

        if (this.loading.value && this.stats.value === null) {
            this.fetchStats();
        }

        return h.div(
            { class: "min-vh-100 py-4 mb-5 py-md-5" },
            h.div(
                { class: "container" },
                // Header with title and offcanvas trigger
                h.div(
                    {
                        class: "mb-3 d-flex align-items-center justify-content-between flex-wrap gap-3",
                    },
                    h.div(
                        { class: "d-flex align-items-center gap-3" },
                        h.button(
                            {
                                class: "btn btn-white border shadow-sm rounded-circle p-2",
                                onclick: "history.back()",
                            },
                            h.SolidIcon("arrow-left"),
                        ),
                        h.h2({ class: "fw-bold mb-0" }, "Analytics"),
                    ),

                    // Filter Trigger Button
                    h.button(
                        {
                            class: "btn btn-white border shadow-sm d-flex align-items-center gap-2 rounded-4 px-3 py-2",
                            onclick: this.method("openFilterForm"),
                        },
                        h.i({ class: "bi bi-sliders fs-5 text-secondary" }),
                        h.span({ class: "fw-bold small" }, "Filter"),
                    ),
                ),

                // Active Filter Badges
                h.div(
                    { class: "mb-4 d-flex flex-wrap gap-2" },
                    h.span(
                        {
                            class: "badge rounded-pill text-dark bg-light border px-3 py-2 fw-normal",
                            onclick: this.method("openFilterForm"),
                        },
                        `${this.startDate.value}`,
                        either(this.endDate.value, [
                            "→",
                            `${this.endDate.value}`,
                        ]),
                    ),
                    h.span(
                        {
                            class: "badge rounded-pill text-dark bg-light border px-3 py-2 fw-normal text-uppercase",
                            onclick: this.method("openFilterForm"),
                        },
                        `Per: ${this.interval.value}`,
                    ),
                ),

                v(this.loading, (loadingState) => {
                    if (loadingState.value) {
                        return h.div(
                            { class: "text-center py-5 my-5" },
                            h.Spinner(),
                            h.p(
                                { class: "text-muted mt-3" },
                                "Loading business analytics...",
                            ),
                        );
                    }

                    return v(this.stats, (statsVal) => {
                        if (!statsVal.value) {
                            return h.div(
                                { class: "text-center py-5 text-muted" },
                                "No analytics data available.",
                            );
                        }

                        const val = statsVal.value;

                        return h.div(
                            h.div(
                                { class: "row g-3 mb-4" },
                                this.renderStatCard(
                                    "Total Orders",
                                    val.total_orders.toString(),
                                    "cart-check",
                                    "primary",
                                ),
                                this.renderStatCard(
                                    "Total Revenue",
                                    `KES ${Number(val.total_revenue).toLocaleString()}`,
                                    "currency-dollar",
                                    "success",
                                ),
                                this.renderStatCard(
                                    "Total Customers",
                                    val.total_customers.toString(),
                                    "person-plus",
                                    "info",
                                ),
                                this.renderStatCard(
                                    "Avg. Order Value",
                                    `KES ${Number(val.avg_order_value).toLocaleString()}`,
                                    "graph-up",
                                    "warning",
                                ),
                            ),

                            h.div(
                                { class: "row g-4" },
                                this.renderChartCard(
                                    "Revenue Trend",
                                    "revenue-chart-canvas",
                                ),
                                this.renderChartCard(
                                    "Orders by Category",
                                    "orders-chart-canvas",
                                ),
                            ),
                        );
                    });
                }),
            ),
        );
    }

    openFilterForm() {
        this.tempStartDate.value = this.startDate.value;
        this.tempEndDate.value = this.endDate.value;
        this.tempInterval.value = this.interval.value;
        this.availableIntervals.value = this.getAvailableIntervals(
            this.startDate.value,
            this.endDate.value,
        );

        if (this.filterHolder) cleanDispose(this.filterHolder);

        const form = () => this.renderFilterForm();

        if (isMobile()) {
            this.filterHolder = tool.offcanvas(h.OffcanvasBody(form()), {
                class: "rounded-top-5 shadow-lg",
                style: "height: auto; max-height: 85vh;",
            });
        } else {
            this.filterHolder = tool.plainModal(h.ModalBody(form()));
        }

        this.filterHolder.show();
    }

    renderFilterForm() {
        return h.div(
            { class: "p-3" },
            h.div(
                {
                    class: "d-flex justify-content-between align-items-center mb-4",
                },
                h.h4({ class: "fw-bold mb-0" }, "Filter Analytics"),
                h.button({
                    class: "btn-close",
                    listeners: { click: () => this.filterHolder.hide() },
                }),
            ),

            h.div(
                { class: "d-grid gap-4" },
                // Start Date
                h.div(
                    { class: "d-flex flex-column gap-2" },
                    h.label(
                        { class: "text-muted small fw-bold text-uppercase" },
                        "Start Date",
                    ),
                    h.input({
                        type: "date",
                        class: "form-control border shadow-none rounded-3",
                        value: this.tempStartDate.value,
                        listeners: {
                            change: (e) =>
                                this.handleTempDateChange(e.target, "start"),
                        },
                    }),
                ),

                // End Date
                h.div(
                    { class: "d-flex flex-column gap-2" },
                    h.label(
                        { class: "text-muted small fw-bold text-uppercase" },
                        "End Date",
                    ),
                    h.input({
                        type: "date",
                        class: "form-control border shadow-none rounded-3",
                        value: this.tempEndDate.value,
                        listeners: {
                            change: (e) =>
                                this.handleTempDateChange(e.target, "end"),
                        },
                    }),
                ),

                // Interval select with dynamic options
                h.div(
                    { class: "d-flex flex-column gap-2" },
                    h.label(
                        { class: "text-muted small fw-bold text-uppercase" },
                        "Interval",
                    ),
                    v(this.availableIntervals, (avail) => {
                        return h.select(
                            {
                                class: "form-select border shadow-none rounded-3",
                                listeners: {
                                    change: (e) => {
                                        this.tempInterval.value =
                                            e.target.value;
                                    },
                                },
                            },
                            ...avail.value.map((opt) =>
                                h.option(
                                    { value: opt },
                                    either(this.tempInterval.value === opt, {
                                        selected: true,
                                    }),
                                    opt.toUpperCase(),
                                ),
                            ),
                        );
                    }),
                ),

                // Apply Button
                h.button(
                    {
                        class: "btn btn-primary rounded-pill p-3 fw-bold shadow-sm mt-2",
                        listeners: {
                            click: () => {
                                this.startDate.value = this.tempStartDate.value;
                                this.endDate.value = this.tempEndDate.value;
                                this.interval.value = this.tempInterval.value;
                                this.loading.value = true;
                                this.stats.value = null;
                                this.filterHolder.hide();
                            },
                        },
                    },
                    "Apply Filters",
                ),

                // Reset Button
                h.button(
                    {
                        class: "btn btn-light rounded-pill p-3 fw-bold",
                        listeners: {
                            click: () => {
                                this.startDate.value = getPastDate(6);
                                this.endDate.value = getTodayDate();
                                this.interval.value = "day";
                                this.loading.value = true;
                                this.stats.value = null;
                                this.filterHolder.hide();
                            },
                        },
                    },
                    "Reset All",
                ),
            ),
        );
    }

    handleTempDateChange(elem, type) {
        if (type === "start") {
            this.tempStartDate.value = elem.value;
        } else {
            this.tempEndDate.value = elem.value;
        }

        const available = this.getAvailableIntervals(
            this.tempStartDate.value,
            this.tempEndDate.value,
        );
        this.availableIntervals.value = available;
        if (!available.includes(this.tempInterval.value)) {
            this.tempInterval.value = available[0];
        }
    }

    renderStatCard(title, value, icon, color) {
        return h.div(
            { class: "col-6 col-md-6 col-lg-3" },
            h.div(
                { class: "card border-0 shadow-sm rounded-4 h-100" },
                h.div(
                    { class: "card-body p-3 p-md-4" },
                    h.div(
                        {
                            class: `bg-${color}-subtle text-${color} rounded-3 d-inline-flex p-2 mb-3`,
                        },
                        h.i({ class: `bi bi-${icon} fs-4` }),
                    ),
                    h.h6(
                        {
                            class: "text-muted small text-uppercase fw-bold mb-1",
                        },
                        title,
                    ),
                    h.h3({ class: "fw-bold mb-0" }, value),
                ),
            ),
        );
    }

    renderChartCard(title, canvasId) {
        return h.div(
            { class: "col-12 col-lg-6" },
            h.div(
                { class: "card border-0 shadow-sm rounded-4 h-100" },
                h.div(
                    { class: "card-body p-4" },
                    h.h5({ class: "fw-bold mb-4" }, title),
                    h.div(
                        { style: "position: relative; height: 300px;" },
                        h.canvas({ id: canvasId }),
                    ),
                ),
            ),
        );
    }

    async fetchStats() {
        try {
            if (window.currentCompany?.id) {
                const res = await api.get(
                    routes.businessAnalytics(window.currentCompany.id),
                    {
                        start_date: this.startDate.value,
                        end_date: this.endDate.value,
                        interval: this.interval.value,
                    },
                );
                if (res) {
                    this.stats.value = res.data;
                }
            }
        } catch (e) {
            console.error(e);
        } finally {
            this.loading.value = false;
        }
    }

    getAvailableIntervals(startStr, endStr) {
        const start = new Date(startStr);
        const end = new Date(endStr);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));

        if (diffDays > 365) {
            return ["year", "month"];
        } else if (diffDays >= 180) {
            return ["month", "week"];
        } else if (diffDays >= 30) {
            return ["month", "week", "day"];
        } else if (diffDays >= 7) {
            return ["week", "day"];
        } else if (diffDays >= 1) {
            return ["day"];
        } else if (diffHours >= 2) {
            return ["hour"];
        } else {
            return ["minute"];
        }
    }

    initRevenueChart(labels, data) {
        const ctx = document.getElementById("revenue-chart-canvas");
        if (!ctx) return;

        if (this.revenueChart) {
            this.revenueChart.destroy();
        }

        this.revenueChart = new Chart(ctx, {
            type: "line",
            data: {
                labels: labels,
                datasets: [
                    {
                        label: "Revenue",
                        data: data,
                        borderColor: "#973e87",
                        backgroundColor: "rgba(151, 62, 135, 0.1)",
                        fill: true,
                        tension: 0.4,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                },
                scales: {
                    y: { beginAtZero: true, grid: { display: false } },
                    x: { grid: { display: false } },
                },
            },
        });
    }

    initOrdersChart(labels, data) {
        const ctx = document.getElementById("orders-chart-canvas");
        if (!ctx) return;

        if (this.ordersChart) {
            this.ordersChart.destroy();
        }

        this.ordersChart = new Chart(ctx, {
            type: "doughnut",
            data: {
                labels: labels,
                datasets: [
                    {
                        data: data,
                        backgroundColor: [
                            "#973e87",
                            "#fed27d",
                            "#0dcaf0",
                            "#dee2e6",
                            "#ffc107",
                        ],
                        borderWidth: 0,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { position: "bottom" },
                },
                cutout: "70%",
            },
        });
    }

    cleanUp() {
        if (this.revenueChart) this.revenueChart.destroy();
        if (this.ordersChart) this.ordersChart.destroy();
        super.cleanUp();
    }
}

ojs(BusinessAnalytics);
